export interface SavingsProjection {
  totalSavings: number;
  investmentGrowth: number;
  totalAmount: number;
  yearlyBreakdown: {
    year: number;
    totalSaved: number;
    investmentValue: number;
  }[];
}

export const calculateSavingsGrowth = (
  monthlySavings: number,
  annualReturnRate: number,
  years: number
): SavingsProjection => {
  const monthlyRate = annualReturnRate / 100 / 12;
  const months = years * 12;

  let totalAmount = 0;
  const yearlyBreakdown = [];

  // Calculate future value of monthly contributions with compound interest
  for (let month = 1; month <= months; month++) {
    totalAmount = (totalAmount + monthlySavings) * (1 + monthlyRate);

    // Record yearly snapshots
    if (month % 12 === 0) {
      yearlyBreakdown.push({
        year: month / 12,
        totalSaved: monthlySavings * month,
        investmentValue: totalAmount,
      });
    }
  }

  const totalSavings = monthlySavings * months;
  const investmentGrowth = totalAmount - totalSavings;

  return {
    totalSavings,
    investmentGrowth,
    totalAmount,
    yearlyBreakdown,
  };
};

export const calculateDownPaymentPercentage = (
  downPayment: number,
  homePrice: number
): number => {
  return (downPayment / homePrice) * 100;
};

export const getDownPaymentStatus = (percentage: number): {
  level: string;
  message: string;
  color: string;
} => {
  if (percentage >= 20) {
    return {
      level: 'Excellent',
      message: "You'll avoid CMHC insurance premiums!",
      color: 'text-emerald-600 dark:text-emerald-400',
    };
  } else if (percentage >= 10) {
    return {
      level: 'Good',
      message: "You're in a strong position with reduced CMHC fees.",
      color: 'text-blue-600 dark:text-blue-400',
    };
  } else if (percentage >= 5) {
    return {
      level: 'Minimum',
      message: 'You meet the minimum requirement, but CMHC fees will apply.',
      color: 'text-amber-600 dark:text-amber-400',
    };
  } else {
    return {
      level: 'Below Minimum',
      message: 'Keep saving! You need at least 5% down payment.',
      color: 'text-red-600 dark:text-red-400',
    };
  }
};
